import React from 'react';

import { css } from '@emotion/css';
import { AggregatedStockAndQuantityFieldsDTO } from '@recurrency/core-api-schema/dist/itemLocations/getItemLocations';
import { GetReplenishmentRulesLocationDTO } from '@recurrency/core-api-schema/dist/replenishmentRules/getReplenishmentRules';
import { DataNode } from 'antd/lib/tree';

import { ActionButton } from 'components/Button/ActionButton';
import { NetStockRecord } from 'components/recipes/equation/NetStockPopover';

import { BaseReplenishmentPathsTreeProps } from './EditReplenishmentPathsTree';

const DEFAULT_TRUNCATE_LIMIT = 3;

export interface ViewReplenishmentPathsTreeProps extends BaseReplenishmentPathsTreeProps {
  replenishmentPathTree: GetReplenishmentRulesLocationDTO[];
  requirementLocationId?: string;
  viewAllOverride?: boolean | undefined;
  onViewAll?: () => void;
  truncate?: boolean;
  getCustomTitle?: (
    node: GetReplenishmentRulesLocationDTO,
    options: ViewReplenishmentPathsTreeProps,
  ) => React.ReactNode;
  truncateLimit?: number;
  customTitleJsx?: React.ReactNode;
}

export const createDefaultTitle = (
  node: GetReplenishmentRulesLocationDTO,
  options: ViewReplenishmentPathsTreeProps,
) => {
  const { replenishmentPathTree, requirementLocationId } = options;

  const isPurchaseLocation = replenishmentPathTree[0]?.locationId === node.locationId;
  const isRequirementLocation = requirementLocationId === node.locationId;
  return (
    <span
      className={css`
        padding-right: 20px;
        display: inline;
        position: relative;
        font-weight: ${isRequirementLocation ? 'bold' : 'normal'};
      `}
    >
      <>
        {node.locationId}: {node.locationName}
        <span>
          {(() => {
            if (isPurchaseLocation && isRequirementLocation) {
              return ' (Purchase & Requirement Location)';
            }
            if (isRequirementLocation) {
              return ' (Requirement Location)';
            }
            if (isPurchaseLocation) {
              return ' (Purchase Location)';
            }
            return null;
          })()}
          {options.customTitleJsx}
        </span>
      </>
    </span>
  );
};

export function convertReplenishmentPathToTree(
  nodes: GetReplenishmentRulesLocationDTO[],
  options: ViewReplenishmentPathsTreeProps,
): DataNode[] {
  const { truncate, onViewAll, viewAllOverride, getCustomTitle, truncateLimit = DEFAULT_TRUNCATE_LIMIT } = options;
  const customTitleFunction = getCustomTitle ?? createDefaultTitle;

  return [...nodes].map((node) => {
    // Truncate is enabled if the option is true and there are enough children where truncation is feasible
    const shouldTruncate = truncate && node.children && node.children.length > truncateLimit;
    // showViewAll controls whether the View All button should be shown for every truncation, or not shown at all
    // To show the View All button once, set viewAllOverride to false and use an Action Button in a HOC
    const showViewAll = viewAllOverride ?? shouldTruncate;

    const displayedLocations = shouldTruncate ? node.children?.slice(0, truncateLimit) : node.children;

    const baseNode = {
      key: `${node.locationId}: ${node.locationName}`,
      title: customTitleFunction(node, options),
      children:
        displayedLocations && displayedLocations.length > 0
          ? convertReplenishmentPathToTree(displayedLocations, options)
          : [],
    };

    if (showViewAll && node.children) {
      return {
        ...baseNode,
        children: [
          ...(displayedLocations
            ? convertReplenishmentPathToTree(displayedLocations, { ...options, viewAllOverride: false })
            : []),
          {
            key: 'view-all',
            title: <ActionButton label={`View All (${node.children?.length} total)`} onClick={onViewAll} />,
          },
        ],
      };
    }

    return baseNode;
  });
}

export const mapAggregatedToNetStockRecord = (
  aggregatedFields: AggregatedStockAndQuantityFieldsDTO,
): Omit<NetStockRecord, 'unitOfMeasure'> => ({
  netStock: aggregatedFields.aggregatedNetStock,
  aggregatedNetStock: aggregatedFields.aggregatedNetStock,
  qtyOnHand: aggregatedFields.aggregatedQtyOnHand,
  qtyAllocated: aggregatedFields.aggregatedQtyAllocated,
  qtyOnPurchaseOrder: aggregatedFields.aggregatedQtyOnPurchaseOrder,
  qtyOnUnapprovedPurchaseOrder: aggregatedFields.aggregatedQtyOnUnapprovedPurchaseOrder,
  qtyInTransit: aggregatedFields.aggregatedQtyInTransit,
  qtyInProcess: aggregatedFields.aggregatedQtyInProcess,
  qtyBackordered: aggregatedFields.aggregatedQtyBackordered,
  qtyReservedDueIn: aggregatedFields.aggregatedQtyReservedDueIn,
  qtyInProduction: aggregatedFields.aggregatedQtyInProduction,
  qtyForProduction: aggregatedFields.aggregatedQtyForProduction,
  qtyQuarantined: aggregatedFields.aggregatedQtyQuarantined,
  qtyOnReleaseSchedule: aggregatedFields.aggregatedQtyOnReleaseSchedule,
  qtyOnPendingBackorder: aggregatedFields.aggregatedQtyOnPendingBackorder,
  qtyOnBlanketPurchaseOrder: aggregatedFields.aggregatedQtyOnBlanketPurchaseOrder,
  qtyFrozen: aggregatedFields.aggregatedQtyFrozen,
  qtyNonPickable: aggregatedFields.aggregatedQtyNonPickable,
});
